/**
 * Browser-side search, filter and sort for the one-page Proof Library. Pure functions so the page
 * and its tests share one definition.
 */
import { valueAreaLabel } from '@/constants/value-areas';
import type { ProofLibraryRow } from '@/lib/proof-library/library-contract';
import { isProofReady, proofClearedForKeys, proofLibraryStatusLabel, type ProofClearedForKey } from '@/lib/utils/proof-readiness';
import { isProofExpirationPast } from '@/lib/utils/proof-metrics-display';

export const PROOF_STRENGTH_LABELS: Record<NonNullable<ProofLibraryRow['proof_strength']>, string> = {
  hard_metric: 'Hard metric',
  case_study: 'Case study',
  survey: 'Survey',
  social_proof: 'Review',
  qualitative: 'Qualitative',
};

export const PROOF_STRENGTH_ORDER: readonly NonNullable<ProofLibraryRow['proof_strength']>[] = [
  'hard_metric', 'case_study', 'survey', 'social_proof', 'qualitative',
];

export const PROOF_SOURCE_TYPE_LABELS: Record<string, string> = {
  performance_brief: 'Performance brief',
  pulse_report: 'Store Pulse report',
  internal_study: 'Internal study',
  customer_provided: 'Customer provided',
  benchmark_aggregate: 'Benchmark aggregate',
  public_web: 'Public web',
};

export const PROOF_ANONYMIZATION_LABELS: Record<ProofLibraryRow['anonymization'], string> = {
  named: 'Named customer',
  anonymized: 'Anonymized',
  aggregate_only: 'Aggregate only',
};

/** Short value-area names for dense table chips; the full labels stay in filters and the detail panel. */
export const VALUE_AREA_SHORT_LABELS: Record<string, string> = {
  discovery: 'Discovery',
  brand_ad_revenue: 'Brand-ad revenue',
  throughput_labor: 'Throughput',
  menu_compliance: 'Menu & pricing',
  loyalty_retention: 'Loyalty',
  fulfillment: 'Fulfillment',
  experience: 'Experience',
  shopper_insight: 'Shopper insight',
};

export function valueAreaShortLabel(slug: string): string {
  return VALUE_AREA_SHORT_LABELS[slug] ?? valueAreaLabel(slug);
}

export function isProofRowArchived(row: Pick<ProofLibraryRow, 'status' | 'archived_at'>): boolean {
  return row.status === 'archived' || Boolean(row.archived_at);
}

export function proofStrengthLabel(strength: ProofLibraryRow['proof_strength']): string {
  return strength ? PROOF_STRENGTH_LABELS[strength] : 'Not set';
}

/** Short strength word for the dense list row ("Hard · E S"); full labels stay in the detail panel. */
export const PROOF_STRENGTH_SHORT_LABELS: Record<NonNullable<ProofLibraryRow['proof_strength']>, string> = {
  hard_metric: 'Hard',
  case_study: 'Case study',
  survey: 'Survey',
  social_proof: 'Review',
  qualitative: 'Qualitative',
};

export function proofStrengthShortLabel(strength: ProofLibraryRow['proof_strength']): string {
  return strength ? PROOF_STRENGTH_SHORT_LABELS[strength] : 'Not set';
}

export function proofSourceTypeLabel(sourceType: string): string {
  return PROOF_SOURCE_TYPE_LABELS[sourceType] ?? sourceType.replace(/_/g, ' ');
}

function formatPeriodDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
}

/** "Jan–Mar 2026" style period text; empty when no dates. */
export function proofPeriodLabel(row: Pick<ProofLibraryRow, 'source_period_start' | 'source_period_end'>): string {
  const start = row.source_period_start ? formatPeriodDate(row.source_period_start) : '';
  const end = row.source_period_end ? formatPeriodDate(row.source_period_end) : '';
  if (start && end) return start === end ? start : `${start} – ${end}`;
  return start || end;
}

export function proofStoreLabel(row: Pick<ProofLibraryRow, 'location_labels' | 'location_ids' | 'anonymization'>): string {
  if (row.location_labels.length > 0) {
    return row.location_labels.length <= 2
      ? row.location_labels.join(', ')
      : `${row.location_labels.slice(0, 2).join(', ')} +${row.location_labels.length - 2}`;
  }
  if (row.location_ids.length > 0) return `${row.location_ids.length} store${row.location_ids.length === 1 ? '' : 's'}`;
  return row.anonymization === 'aggregate_only' ? 'Network-wide' : 'No store';
}

export function proofSourceSummary(row: ProofLibraryRow): string {
  return [proofSourceTypeLabel(row.source_type), proofPeriodLabel(row), proofStoreLabel(row)]
    .filter(Boolean)
    .join(' · ');
}

/** Every field the library's search box matches against, lowercased. Shared so the page can't drift from it. */
export function proofLibrarySearchHaystack(row: ProofLibraryRow): string {
  return [
    row.label_short,
    row.claim_text,
    row.value_display,
    row.proof_metric_id,
    row.source_ref,
    ...row.location_labels,
    ...row.value_areas.map(valueAreaLabel),
    proofStrengthLabel(row.proof_strength),
    proofSourceTypeLabel(row.source_type),
  ].join(' ').toLocaleLowerCase();
}

/** Resolves a `?proof=` value (row uuid or family id) to the row to expand: a family id opens its newest non-archived version. */
export function resolveDeepLinkedRow(rows: readonly ProofLibraryRow[], target: string | null): ProofLibraryRow | null {
  if (!target) return null;
  const byId = rows.find((row) => row.id === target);
  if (byId) return byId;
  const family = rows
    .filter((row) => row.proof_metric_id === target && !isProofRowArchived(row))
    .sort((left, right) => right.version - left.version);
  return family[0] ?? rows.find((row) => row.proof_metric_id === target) ?? null;
}

/** Query parameters the page owns, incl. its saved-view pills. Used only as the open-redirect allowlist for `?returnTo=` (see proof-return-to.ts) - the page itself reads each of these directly. */
export const PROOF_LIBRARY_VIEW_PARAMS = ['q', 'saved', 'location', 'archived'] as const;

export interface ProofLibraryCounts {
  total: number;
  ready: number;
  stale: number;
  archived: number;
}

export function countProofLibraryRows(rows: readonly ProofLibraryRow[]): ProofLibraryCounts {
  let ready = 0;
  let stale = 0;
  let archived = 0;
  for (const row of rows) {
    if (isProofRowArchived(row)) {
      archived += 1;
      continue;
    }
    if (isProofReady(row)) {
      if (isProofExpirationPast(row)) stale += 1;
      else ready += 1;
    } else if (row.status === 'expired') {
      stale += 1;
    }
  }
  return { total: rows.length - archived, ready, stale, archived };
}

/** "+14.67% / +10.35%" -> "+14.67%" for the fixed-width list-row value column; the full value stays in the panel. */
export function proofValueAbbreviated(value: string): string {
  const [first] = value.split('/');
  return (first ?? value).trim() || value;
}

/** "E S" style initials for the list row's trailing meta - E=email, W=website, S=sales deck, in that order; '' when none cleared. */
export function proofClearedForInitials(row: Pick<ProofLibraryRow, 'channels_allowed'>): string {
  const initialByKey: Record<ProofClearedForKey, string> = { email: 'E', website: 'W', sales_deck: 'S' };
  return proofClearedForKeys(row).map((key) => initialByKey[key]).join(' ');
}

/** Spelled-out "Email, deck" form for the table view's Cleared for column. */
export function proofClearedForSpelledOut(row: Pick<ProofLibraryRow, 'channels_allowed'>): string {
  const shortByKey: Record<ProofClearedForKey, string> = { email: 'Email', website: 'Website', sales_deck: 'deck' };
  const keys = proofClearedForKeys(row);
  return keys.length > 0 ? keys.map((key) => shortByKey[key]).join(', ') : 'Internal only';
}

function parseDateOnly(value: string): Date | null {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!dateOnly) return null;
  const [, year, month, day] = dateOnly;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  parsed.setHours(0, 0, 0, 0);
  return parsed;
}

/** Whole days from today to `expires_at` (negative once past); null when there is no expiry. */
export function proofDaysUntilExpiry(row: Pick<ProofLibraryRow, 'expires_at'>): number | null {
  if (!row.expires_at) return null;
  const expires = parseDateOnly(row.expires_at);
  if (!expires) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((expires.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
}

/** True for a proof expiring within 30 days (today through +30), not yet past. Past-due already reads as "Stale" via the status badge. */
export function isProofExpiringSoon(row: Pick<ProofLibraryRow, 'expires_at'>): boolean {
  const days = proofDaysUntilExpiry(row);
  return days !== null && days >= 0 && days <= 30;
}

/** True when a proof has no proof_strength set - the "Needs strength" saved view. */
export function needsStrength(row: Pick<ProofLibraryRow, 'proof_strength'>): boolean {
  return row.proof_strength === null;
}

export type ProofRowMetaTone = 'normal' | 'unset' | 'warning';

/** The list row's trailing meta: expiry warning wins over strength; strength/cleared-for otherwise. */
export function proofRowMeta(row: Pick<ProofLibraryRow, 'proof_strength' | 'expires_at' | 'channels_allowed'>): { text: string; tone: ProofRowMetaTone } {
  const days = proofDaysUntilExpiry(row);
  if (days !== null && days >= 0 && days <= 30) return { text: `Expires in ${days}d`, tone: 'warning' };
  const initials = proofClearedForInitials(row);
  const strength = proofStrengthShortLabel(row.proof_strength);
  const text = initials ? `${strength} · ${initials}` : strength;
  return { text, tone: row.proof_strength === null ? 'unset' : 'normal' };
}

/** First value area is primary for grouping; a proof with none groups under "Untagged". */
export function proofPrimaryValueArea(row: Pick<ProofLibraryRow, 'value_areas'>): string | null {
  return row.value_areas[0] ?? null;
}

export interface ProofValueAreaGroup {
  slug: string | null;
  label: string;
  rows: ProofLibraryRow[];
}

/** Groups by primary (first) value area, ordered by group size desc; ungrouped proofs form a trailing "Untagged" group. */
export function groupProofsByPrimaryValueArea(rows: readonly ProofLibraryRow[]): ProofValueAreaGroup[] {
  const bySlug = new Map<string | null, ProofLibraryRow[]>();
  for (const row of rows) {
    const slug = proofPrimaryValueArea(row);
    const list = bySlug.get(slug) ?? [];
    list.push(row);
    bySlug.set(slug, list);
  }
  const groups: ProofValueAreaGroup[] = Array.from(bySlug.entries()).map(([slug, groupRows]) => ({
    slug,
    label: slug ? valueAreaLabel(slug) : 'Untagged',
    rows: groupRows,
  }));
  groups.sort((left, right) => {
    if (left.slug === null) return 1;
    if (right.slug === null) return -1;
    return right.rows.length - left.rows.length;
  });
  return groups;
}

/** Text the "Copy claim" menu's three formats produce. */
export type ProofClaimCopyFormat = 'claim' | 'citation' | 'link';

export function proofClaimCopyText(row: ProofLibraryRow, format: ProofClaimCopyFormat, sourceHref: string | null): string {
  if (format === 'claim') return row.claim_text;
  if (format === 'citation') {
    const period = proofPeriodLabel(row);
    const stores = proofStoreLabel(row);
    const parts = [period, stores].filter(Boolean).join(', ');
    const naming = row.anonymization === 'named' && row.named_use_ok
      ? null
      : row.anonymization === 'aggregate_only' ? 'aggregate, not attributed to a named customer'
        : row.anonymization === 'anonymized' ? 'anonymized, not attributed to a named customer'
          : null;
    return [row.claim_text, parts ? `(${parts})` : '', naming ? `(${naming})` : ''].filter(Boolean).join(' ');
  }
  return sourceHref ? `${row.claim_text}\nSource: ${sourceHref}` : row.claim_text;
}

export { proofLibraryStatusLabel };
